import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useActionsContext } from '@cotera/client/v0/actions/framework';
import { AgentModel } from '@cotera/client/app/v1/agent/agent.model';
import { DocumentModel } from '@cotera/client/app/v1/document/document.model';
import { WorkflowModel } from '@cotera/client/app/v1/workflow/workflow.model';
import { collectDirtyResourceIds } from './artifact-dirty-state';

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Subscribes to every in-scope savable model and recomputes the dirty set when
 * any of them changes.
 *
 * Private to this module: it is the *provider's* implementation, and there must
 * be exactly one of it per panel. Model scope here is flat — `ModelScope`,
 * `ModelScopeFactory` and `Actions` all register into the one root
 * `ActionsRegistryProvider` rather than creating child scopes — so every caller
 * would resolve the same models, subscribe to the same watchables and build the
 * same `Set` again. Three call sites did exactly that, one of them (the
 * artifacts index card) from a component that renders `null` whenever the
 * Canvas is open.
 */
const useDirtyArtifactResourceIdsSubscription = (): ReadonlySet<string> => {
  const actionsContext = useActionsContext();
  const [modelsEpoch, setModelsEpoch] = useState(0);
  const [dirtyVersion, setDirtyVersion] = useState(0);

  useEffect(() => {
    return actionsContext.subscribe(() => {
      setModelsEpoch((n) => n + 1);
    });
  }, [actionsContext]);

  useEffect(() => {
    const bump = (): void => {
      setDirtyVersion((n) => n + 1);
    };
    const unsubs: Array<() => void> = [];
    for (const agent of actionsContext.getInScopeModelsOfType(AgentModel)) {
      unsubs.push(agent.hasUnsavedChangesWatchable.subscribe(bump));
    }
    for (const document of actionsContext.getInScopeModelsOfType(
      DocumentModel
    )) {
      unsubs.push(document.hasUnsavedChangesWatchable.subscribe(bump));
    }
    for (const workflow of actionsContext.getInScopeModelsOfType(
      WorkflowModel
    )) {
      unsubs.push(workflow.hasUnsavedChangesWatchable.subscribe(bump));
    }
    bump();
    return () => {
      for (const unsub of unsubs) {
        unsub();
      }
    };
  }, [actionsContext, modelsEpoch]);

  return useMemo(() => {
    void dirtyVersion;
    return collectDirtyResourceIds(actionsContext);
  }, [actionsContext, dirtyVersion]);
};

const DirtyArtifactResourceIdsContext =
  createContext<ReadonlySet<string> | null>(null);

/**
 * Publishes the dirty set once for the whole panel.
 *
 * Mounted by `ArtifactSidePanel`, above both the mobile and desktop branches, so
 * the three things that draw dirty dots — the panel's artifact label, the bar's
 * switcher menu, and the artifacts index card — share one subscription instead
 * of standing up their own. The label in particular renders once per artifact,
 * so the old arrangement scaled the subscription work with the artifact count.
 */
export const DirtyArtifactResourceIdsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dirtyResourceIds = useDirtyArtifactResourceIdsSubscription();
  return (
    <DirtyArtifactResourceIdsContext.Provider value={dirtyResourceIds}>
      {children}
    </DirtyArtifactResourceIdsContext.Provider>
  );
};

/**
 * Resource ids from in-scope savable models that currently have unsaved changes.
 *
 * Used only to draw dirty dots — Ctrl+S ownership stays on
 * `SaveChatArtifactsAction`. Empty outside a
 * {@link DirtyArtifactResourceIdsProvider}, which matches the rest of the
 * panel's context hooks: a component rendered away from the panel draws no dots
 * rather than throwing.
 */
export function useDirtyArtifactResourceIds(): ReadonlySet<string> {
  return useContext(DirtyArtifactResourceIdsContext) ?? EMPTY;
}
